// src/services/api/appointmentService.ts
import {
  Appointment,
  AppointmentCustomerFreeReq,
  AppointmentMonthlyStatsResp,
  AppointmentReq,
  AppointmentStatus,
  AvailableTimeSlotsReq,
  AvailableTimeSlotsResp,
  CustomerResp,
} from "../../interfaces/services/Appointment";
import apiUrls from "../../store/apiUrls";
import apiService, { apiCachedGet, invalidateCachePrefix } from "./apiService";

export interface AppointmentFilters {
  customerCode?: string;
  employeeCode?: string;
  serviceCode?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

const APPT_NS = "APPT:";

const CK = {
  listBySalon: (salonCode: string) => `${APPT_NS}list:salon:${salonCode}`,
  listBySalonFiltered: (salonCode: string, query: string) =>
    `${APPT_NS}list:salon:${salonCode}?${query}`,
  countFiltered: (query: string) => `${APPT_NS}count?${query}`,
  item: (code: string) => `${APPT_NS}item:${code}`,
  customersBySalon: (query: string) => `${APPT_NS}customers?${query}`,
  byCustomer: (customerCode: string) => `${APPT_NS}byCustomer:${customerCode}`,
  myAppointments: () => `${APPT_NS}my`,
  availableSlots: (query: string) => `${APPT_NS}available:${query}`,
  stats: (date: string) => `${APPT_NS}date:${date}`,
};

function toAvailableQuery(q: AvailableTimeSlotsReq) {
  const p = new URLSearchParams();
  p.append("Date", q.date);
  p.append("EmployeeCode", q.employeeCode);
  p.append("OperatingHours.Day", q.day);
  p.append("OperatingHours.OpenTime", q.openTime);
  p.append("OperatingHours.CloseTime", q.closeTime);

  p.append("ServiceDuration", String(q.serviceDuration));
  return p.toString();
}

/** ---- Ortak: filtreleri deterministik query string’e çevir ---- */
function toQuery(filters: AppointmentFilters = {}) {
  const p = new URLSearchParams();
  if (filters.customerCode) p.append("customerCode", filters.customerCode);
  if (filters.employeeCode) p.append("employeeCode", filters.employeeCode);
  if (filters.serviceCode) p.append("serviceCode", filters.serviceCode);
  if (filters.status) p.append("status", filters.status);
  if (filters.startDate) p.append("startDate", filters.startDate);
  if (filters.endDate) p.append("endDate", filters.endDate);
  return p.toString(); // deterministik key için kullanıyoruz
}

const appointmentService = {
  /** -------------------- READS (cached) -------------------- */

  // Tüm randevular (salon)
  getAppointmentsWithSalonCode: (salonCode: string): Promise<Appointment[]> => {
    const url = `${apiUrls.appointment.appointment.bySalon}/${salonCode}`;
    return apiCachedGet<Appointment[]>("APPOINTMENT", url, {
      ttl: 10 * 60 * 1000,
      store: "memory",
      keyExtra: CK.listBySalon(salonCode),
      swr: false,
    });
  },

  // Filtreli liste
  getFilteredAppointments: (
    salonCode: string,
    filters: AppointmentFilters = {}
  ): Promise<Appointment[]> => {
    const query = toQuery(filters);
    const url = `${
      apiUrls.appointment.appointment.bySalonFiltered
    }/${salonCode}${query ? `?${query}` : ""}`;
    return apiCachedGet<Appointment[]>("APPOINTMENT", url, {
      ttl: 5 * 60 * 1000,
      store: "memory",
      keyExtra: CK.listBySalonFiltered(salonCode, query),
      swr: false,
    });
  },

  // Kod ile tekil randevu
  getAppointmentCode: (code: string): Promise<Appointment> => {
    const url = `${apiUrls.appointment.appointment.appointment}/${code}`;
    return apiCachedGet<Appointment>("APPOINTMENT", url, {
      ttl: 5 * 60 * 1000,
      store: "memory",
      keyExtra: CK.item(code),
      swr: false,
    });
  },

  // Salon müşterileri (öneri/list)
  getCustomersBySalonCode: (
    salonCode: string,
    count?: number,
    startDate?: string,
    endDate?: string
  ): Promise<CustomerResp[]> => {
    const query = new URLSearchParams({
      salonCode,
      ...(count !== undefined ? { count: String(count) } : {}),
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
    }).toString();

    const url = `${apiUrls.appointment.appointment.customersBySalon}?${query}`;
    return apiCachedGet<CustomerResp[]>("APPOINTMENT", url, {
      ttl: 10 * 60 * 1000,
      store: "memory",
      keyExtra: CK.customersBySalon(query),
      swr: false,
    });
  },

  // Müşterinin randevuları
  getAppointmentsByCustomerCode: (
    customerCode: string
  ): Promise<Appointment[]> => {
    const url = `${apiUrls.appointment.appointment.appointmentsByCustomer}/${customerCode}`;
    return apiCachedGet<Appointment[]>("APPOINTMENT", url, {
      ttl: 10 * 60 * 1000,
      store: "memory",
      keyExtra: CK.byCustomer(customerCode),
      swr: false,
    });
  },

  // Müşterinin randevuları
  getMyAppointments: (): Promise<Appointment[]> => {
    const url = `${apiUrls.appointment.appointment.myAppointments}`;
    return apiCachedGet<Appointment[]>("APPOINTMENT", url, {
      ttl: 10 * 60 * 1000,
      store: "memory",
      keyExtra: CK.myAppointments(),
      swr: false,
    });
  },

  getAvailableTimeSlots: (
    params: AvailableTimeSlotsReq
  ): Promise<AvailableTimeSlotsResp> => {
    const query = toAvailableQuery(params);
    const url = `${apiUrls.appointment.appointment.availableTimeSlots}${
      query ? `?${query}` : ""
    }`;
    return apiCachedGet<AvailableTimeSlotsResp>("APPOINTMENT", url, {
      ttl: 60 * 1000, // slotlar hızlı değişir: 1 dk makul
      store: "memory",
      keyExtra: CK.availableSlots(query), // 🔑 her kombinasyon ayrı cache
      swr: true, // varsa cache’i hemen döndür + arkaplanda tazele
    });
  },
  /** -------------------- MUTATIONS (invalidate) -------------------- */
  // Hepsinde tüm randevu cache’ini siliyoruz (APPT_NS)
  createAppointment: async (appointment: AppointmentReq): Promise<string> => {
    const res = await apiService.APPOINTMENT.post(
      `${apiUrls.appointment.appointment.appointment}`,
      appointment
    );
    invalidateCachePrefix(APPT_NS);
    return res;
  },

  createAppointmentCustomerFree: async (
    appointment: AppointmentCustomerFreeReq
  ): Promise<string> => {
    const res = await apiService.APPOINTMENT.post(
      `${apiUrls.appointment.appointment.appointmentCustomerFree}`,
      appointment
    );
    invalidateCachePrefix(APPT_NS);
    return res;
  },

  putAppointmentCode: async (
    code: string,
    appointment: AppointmentReq
  ): Promise<string> => {
    const res = await apiService.APPOINTMENT.put(
      `${apiUrls.appointment.appointment.appointment}/${code}`,
      appointment
    );
    invalidateCachePrefix(APPT_NS);
    return res;
  },

  changestatus: async (
    code: string,
    status: AppointmentStatus
  ): Promise<string[]> => {
    const query = new URLSearchParams({ status }).toString();
    const res = await apiService.APPOINTMENT.patch(
      `${apiUrls.appointment.appointment.updateStatus}/${code}?${query}`
    );
    invalidateCachePrefix(APPT_NS);
    return res;
  },

  monthlyStats: async (month: string): Promise<AppointmentMonthlyStatsResp> => {
    const query = new URLSearchParams({ month }).toString();
    const url = `${apiUrls.appointment.appointment.monthlyStats}?${query}`;

    invalidateCachePrefix(APPT_NS);
    return apiCachedGet<AppointmentMonthlyStatsResp>("APPOINTMENT", url, {
      ttl: 10 * 60 * 1000,
      keyExtra: CK.stats(month),
    });
  },
};

export default appointmentService;
